import { Box } from '@mui/material';
import { DocumentStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Navigate, useMatch } from 'react-router-dom';
import { PaloStatusScaleProvider, PaloStatusStep, usePaloHeadSlot } from '@synergy/components-palo';
import { PoolSpinner, UrlFilter } from '@synergy/components';
import { ReactElement, useMemo } from 'react';
import { UserPermission } from '@synergy/models';
import { createPortal } from 'react-dom';
import { hasPermission } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { DashboardFilterModal } from '../components/dashboard-filter-modal';
import { DashboardOverviewOutlet } from '../containers/dashboard-overview-outlet';
import { DashboardTabs } from '../model/dashboard-tabs';
import { PaloCreateNewMenu } from './palo-create-new-menu';
import { PaloDashboardOverviewTabs } from './palo-dashboard-overview-tabs';
import { PaloResponsibleQuickFilter } from '../../../common/components/filter/palo-responsible-quick-filter';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { defaultDashboardSearchParamsSelector } from '../../../store/default-searchparams-selector';
import { requestingSelector } from '../store/dashboard-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { userSelector } from '../../../common/selectors/user-selectors';

const c = tokens.color;

/**
 * In the cockpit an order that ended without a follow-up document is a task: the
 * invoice it should have led to is missing. Purchasing asked for it in red, the
 * way the old dashboard showed it (key-user brief A1.5). The lists keep it green —
 * it covers more than half of all orders there, and red would lose its meaning.
 */
const COCKPIT_STATUS_SCALE: Record<string, string> = {
    [DocumentStatus.CompletedWithoutTransition]: PaloStatusStep.Problem,
};

export const PaloDashboardOverview = (): ReactElement => {
    const requesting = useAppSelector(requestingSelector);
    const isRootLocation = !!useMatch(ROUTES.dashboard);
    const defaultUserSearchParams = useAppSelector(defaultDashboardSearchParamsSelector);
    const user = useAppSelector(userSelector);
    // The cockpit is a list like the others: it names itself in the bar and
    // puts its toolbar there, so the page begins with the tabs.
    const headSlot = usePaloHeadSlot(true);
    const inHeadSlot = null !== headSlot.element;

    const defaultTab = useMemo((): DashboardTabs => {
        if (hasPermission(UserPermission.SalesOrders, user)) {
            return DashboardTabs.SalesOrders;
        } else if (hasPermission(UserPermission.Inquiries, user)) {
            return DashboardTabs.Inquiries;
        } else if (hasPermission(UserPermission.PurchaseOrders, user)) {
            return DashboardTabs.PurchaseOrders;
        } else if (hasPermission(UserPermission.Demands, user)) {
            return DashboardTabs.Demands;
        }

        return DashboardTabs.Invoices;
    }, [user]);

    if (isRootLocation) {
        return <Navigate to={{ pathname: defaultTab, search: defaultUserSearchParams.toString() }} replace={true} />;
    }

    const headRow = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                ...(inHeadSlot ? { width: '100%', minWidth: 0, flexWrap: 'nowrap' } : { mb: 3, flexWrap: 'wrap' }),
            }}
        >
            <Box
                sx={{
                    fontSize: inHeadSlot ? '14px' : '20px',
                    fontWeight: 640,
                    letterSpacing: '-.015em',
                    color: c.ink,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                <FormattedMessage id="app.dashboard.title" />
            </Box>
            <Box
                sx={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    flexShrink: 0,
                }}
            >
                <PaloResponsibleQuickFilter />
                {/* Clearing them all is part of the filter button now, where
                    the count is (#2209) — a second one beside it said the
                    same thing twice. */}
                <DashboardFilterModal />
                <PaloCreateNewMenu />
            </Box>
        </Box>
    );

    return (
        <PaloStatusScaleProvider scale={COCKPIT_STATUS_SCALE}>
            <UrlFilter useLegacyFilter={true}>
                <PoolSpinner enabled={requesting} sx={{ height: '100%' }}>
                    <Box
                        sx={{
                            backgroundColor: c.canvas,
                            minHeight: '100%',
                            p: 4,
                            ...(inHeadSlot && { pt: 2 }),
                        }}
                    >
                        {inHeadSlot ? createPortal(headRow, headSlot.element as HTMLElement) : headRow}

                        <PaloDashboardOverviewTabs />

                        <Box sx={{ mt: 2 }}>
                            <DashboardOverviewOutlet />
                        </Box>
                    </Box>
                </PoolSpinner>
            </UrlFilter>
        </PaloStatusScaleProvider>
    );
};
